import { useRef, useEffect, useCallback, useState } from 'react'

interface UseWebSocketOptions {
  url: string
  onMessage: (data: any) => void
  onConnect?: () => void
  onDisconnect?: () => void
  reconnectInterval?: number
}

export function useWebSocket({
  url,
  onMessage,
  onConnect,
  onDisconnect,
  reconnectInterval = 3000,
}: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [status, setStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected')
  const isManualClose = useRef(false)

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    try {
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('WebSocket connected')
        setStatus('connected')
        onConnect?.()
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          onMessage(data)
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err)
        }
      }

      ws.onclose = () => {
        setStatus('disconnected')
        onDisconnect?.()

        if (!isManualClose.current) {
          // Auto reconnect
          reconnectTimerRef.current = setTimeout(() => {
            connect()
          }, reconnectInterval)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        setStatus('error')
      }
    } catch (err) {
      console.error('Failed to create WebSocket:', err)
      setStatus('error')
    }
  }, [url, onMessage, onConnect, onDisconnect, reconnectInterval])

  const disconnect = useCallback(() => {
    isManualClose.current = true

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }

    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
  }, [])

  const sendMessage = useCallback((message: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(message)
    } else {
      console.warn('WebSocket not connected, message not sent')
    }
  }, [])

  const sendBytes = useCallback((data: Uint8Array) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(data)
    } else {
      console.warn('WebSocket not connected, bytes not sent')
    }
  }, [])

  // Connect on mount
  useEffect(() => {
    isManualClose.current = false
    connect()

    return () => {
      disconnect()
    }
  }, [connect, disconnect])

  return {
    status,
    sendMessage,
    sendBytes,
    connect,
    disconnect,
  }
}

export default useWebSocket
